/**
 * Canonical editor plain-text coordinates for agent wire context.
 * Matches TipTap `doc.textBetween(0, pos, "\n")` — single `\n` between blocks.
 * Do not use `editor.getText()` (`\n\n` block separators) for offset math.
 */
export const EDITOR_PLAIN_TEXT_BLOCK_SEPARATOR = "\n";

export type EditorPlainTextSource = {
  textBetween: (
    from: number,
    to: number,
    blockSeparator?: string,
    leafSeparator?: string,
  ) => string;
  content: { size: number };
};

export function buildEditorPlainText(doc: EditorPlainTextSource): string {
  return doc.textBetween(0, doc.content.size, EDITOR_PLAIN_TEXT_BLOCK_SEPARATOR);
}

export function plainTextRangeForPmSelection(
  doc: EditorPlainTextSource,
  pmFrom: number,
  pmTo: number,
): { start: number; end: number } {
  const sep = EDITOR_PLAIN_TEXT_BLOCK_SEPARATOR;
  return {
    start: doc.textBetween(0, pmFrom, sep).length,
    end: doc.textBetween(0, pmTo, sep).length,
  };
}

/** Map a wire plain-text index to the PM position with the same `textBetween` prefix length. */
export function pmPositionAtEditorPlainIndex(
  doc: EditorPlainTextSource,
  plainIndex: number,
): number | null {
  const target = Math.max(0, Math.floor(plainIndex));
  const sep = EDITOR_PLAIN_TEXT_BLOCK_SEPARATOR;
  const size = Math.max(0, Math.floor(doc.content.size));
  if (size === 0) return target === 0 ? 0 : null;

  let lo = 0;
  let hi = size;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const len = doc.textBetween(0, mid, sep).length;
    if (len < target) lo = mid + 1;
    else hi = mid;
  }
  if (lo > size) return null;
  return doc.textBetween(0, lo, sep).length === target ? lo : null;
}

export function pmRangeForEditorPlainRange(
  doc: EditorPlainTextSource,
  plainStart: number,
  plainEnd: number,
): { from: number; to: number } | null {
  if (
    !Number.isFinite(plainStart)
    || !Number.isFinite(plainEnd)
    || plainEnd <= plainStart
  ) {
    return null;
  }
  const from = pmPositionAtEditorPlainIndex(doc, plainStart);
  const to = pmPositionAtEditorPlainIndex(doc, plainEnd);
  if (from == null || to == null || to <= from) return null;
  return { from, to };
}

export type EditorPlainHintRange = {
  start: number;
  end: number;
  text: string;
};

/** Validate wire `plainTextStart`/`plainTextEnd` against an editor plain-text body. */
export function readEditorPlainHintRange(params: {
  body: string;
  plainTextStart?: number;
  plainTextEnd?: number;
  passage: string;
  matchesPassage?: (slice: string, passage: string) => boolean;
}): EditorPlainHintRange | null {
  const startRaw = params.plainTextStart;
  const endRaw = params.plainTextEnd;
  const passage = String(params.passage ?? "").trim();
  if (!passage) return null;
  if (
    typeof startRaw !== "number"
    || typeof endRaw !== "number"
    || !Number.isFinite(startRaw)
    || !Number.isFinite(endRaw)
    || endRaw <= startRaw
  ) {
    return null;
  }

  const body = String(params.body ?? "");
  const start = Math.max(0, Math.floor(startRaw));
  const end = Math.min(body.length, Math.floor(endRaw));
  if (end <= start || end > body.length) return null;

  const text = body.slice(start, end);
  const matches = params.matchesPassage ?? defaultPlainPassageMatch;
  if (!text.trim() || !matches(text, passage)) return null;

  return { start, end, text };
}

function defaultPlainPassageMatch(slice: string, passage: string): boolean {
  const s = String(slice ?? "").trim();
  const p = String(passage ?? "").trim();
  if (!s || !p) return false;
  if (s === p) return true;
  return p.includes(s) || s.includes(p);
}
